import React, { useMemo, useState, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapse } from 'react-bootstrap';
import type { Subsection } from '../../utils/subsectionUtils';
import type { ComponentWithReplacement } from '../../types';
import type { ComponentMaintenanceData } from '../../../../../Helpers/maintenanceCostUtils';
import { groupSubsectionsByContext } from '../../utils/subsectionUtils';
import { IndeterminateCheckbox } from './IndeterminateCheckbox';

/** Massredigering: a select-all checkbox on every type header */
export interface SubsectionSelection {
    /** The keys the header's checkbox governs (the subsection's components of the selectable type) */
    selectableKeys: (components: ComponentWithReplacement[]) => string[];
    isSelected: (componentKey: string) => boolean;
    setSelected: (componentKeys: string[], selected: boolean) => void;
}

interface SubsectionedListProps {
    subsections: Subsection[];
    renderItem: (plan: ComponentWithReplacement) => React.ReactNode;
    draftComponents?: Record<string, ComponentMaintenanceData>;
    selection?: SubsectionSelection;
}

/**
 * Renders components grouped by context (direction or room) and then by type.
 * Creates a two-level visual hierarchy with context headers and type subsections.
 * Subsections are collapsible.
 */
export const SubsectionedList: React.FC<SubsectionedListProps> = ({ subsections, renderItem, draftComponents, selection }) => {
    // Track collapsed subsections by key
    const [collapsedSubsections, setCollapsedSubsections] = useState<Set<string>>(new Set());

    const toggleSubsection = useCallback((key: string) => {
        setCollapsedSubsections(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    // Group subsections by context for visual hierarchy
    const byContext = useMemo(() => groupSubsectionsByContext(subsections), [subsections]);

    // Check if all components in a context are processed (done or excluded)
    const isContextProcessed = useCallback((contextSubs: Subsection[]): boolean => {
        if (!draftComponents) return false;
        const allComponents = contextSubs.flatMap(sub => sub.components);
        if (allComponents.length === 0) return false;
        return allComponents.every(comp => {
            const data = draftComponents[comp.component.key];
            return data?.isDone === true || data?.excluded === true;
        });
    }, [draftComponents]);

    return (
        <>
            {Array.from(byContext.entries()).map(([contextKey, contextSubs]) => {
                const allProcessed = isContextProcessed(contextSubs);
                return (
                <div key={contextKey} className="component-context-group">
                    <div className={`component-context-header ${allProcessed ? 'all-processed' : ''}`}>
                        {contextSubs[0].contextLabel}
                    </div>
                    {contextSubs.map(sub => {
                        const isCollapsed = collapsedSubsections.has(sub.key);
                        const selectable = selection ? selection.selectableKeys(sub.components) : [];
                        const selectedCount = selectable.filter(selection ? selection.isSelected : () => false).length;
                        return (
                            <div key={sub.key} className="component-subsection">
                                <div
                                    className="component-subsection-header"
                                    onClick={() => toggleSubsection(sub.key)}
                                >
                                    <ChevronDown
                                        size={14}
                                        className={`component-subsection-chevron ${isCollapsed ? 'collapsed' : ''}`}
                                    />
                                    {selection && (
                                        <IndeterminateCheckbox
                                            className="form-check-input component-subsection-check"
                                            checked={selectable.length > 0 && selectedCount === selectable.length}
                                            indeterminate={selectedCount > 0 && selectedCount < selectable.length}
                                            disabled={selectable.length === 0}
                                            aria-label={`Välj alla ${sub.typeLabel} i ${sub.contextLabel}`}
                                            title={selectable.length === 0 ? 'Komponenterna är av en annan typ än de valda' : undefined}
                                            onChange={(e) => selection.setSelected(selectable, e.target.checked)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    )}
                                    <span className="component-subsection-type">{sub.typeLabel}</span>
                                    <span className="component-subsection-count">
                                        ({sub.components.length})
                                    </span>
                                </div>
                                <Collapse in={!isCollapsed}>
                                    <div>
                                        {sub.components.map(renderItem)}
                                    </div>
                                </Collapse>
                            </div>
                        );
                    })}
                </div>
                );
            })}
        </>
    );
};

export default SubsectionedList;
